var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { yupResolver } from '@hookform/resolvers/yup';
import { camelCase } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { DEFAULT_GENESYS_REGION_HOST } from '../../../constants/genesysRegions';
import { credentialFieldsFor } from '../../../constants/integrationCredentials';
import { useIndustryAPI } from '../../manageUsers/selectors';
import { MASKED_SECRET } from '../constants';
import { useAvailableBusinessUnitsAPI, useClientBusinessUnitsAPI, useClientDetailAPI, useCreateClientAPI, useDisconnectIntegrationAPI, useImportBusinessUnitsAPI, useRetestConnectionAPI, useTestAndSaveConnectionAPI, useTestConnectionAPI, useUpdateClientAPI, } from './selectors';
import { useBusinessUnitSelection } from './useBusinessUnitSelection';
export var defaultIntegration = {
    type: '',
    name: '',
    clientId: '',
    clientSecret: '',
    region: DEFAULT_GENESYS_REGION_HOST,
    apiBaseUrl: '',
    tokenUrl: '',
};
var isActualIntegration = function (type) { return type !== '' && type !== 'none'; };
/** A client's configured connection by row id, if any. */
var findClientIntegrationById = function (client, integrationId) { var _a; return integrationId ? (_a = client === null || client === void 0 ? void 0 : client.integrations) === null || _a === void 0 ? void 0 : _a.find(function (i) { return i.id === integrationId; }) : undefined; };
var getInitialTestResult = function (integrationStatus) {
    return integrationStatus === 'connected' ? { status: 'success' } : null;
};
var needsFetchClientDetail = function (open, editClient, target, integrationType, clientId, tokenUrl) {
    if (!open || !editClient)
        return false;
    if ((target === null || target === void 0 ? void 0 : target.integrationStatus) !== 'connected' && (target === null || target === void 0 ? void 0 : target.integrationStatus) !== 'failed')
        return false;
    if (integrationType !== 'genesys' && integrationType !== 'aspect')
        return false;
    return (integrationType === 'genesys' && !clientId) || (integrationType === 'aspect' && !tokenUrl);
};
var credentialValue = function (form, name) { var _a; return (_a = form[name]) !== null && _a !== void 0 ? _a : ''; };
var hasValue = function (value) { return !!value.trim(); };
var areCredentialsComplete = function (type, form, isAlreadyConnected) {
    if (isAlreadyConnected)
        return true;
    if (!form.name.trim() && isActualIntegration(type))
        return false;
    return credentialFieldsFor(type)
        .filter(function (field) { return field.required; })
        .every(function (field) { return !!credentialValue(form, field.name).trim(); });
};
var buildCredentials = function (integration, isAlreadyConnected) {
    if (isAlreadyConnected)
        return null;
    var fields = credentialFieldsFor(integration.type);
    if (!fields.length)
        return null;
    // Optional fields are omitted when blank — the API rejects a supplied-but-empty value.
    return Object.fromEntries(fields
        .map(function (field) { return [field.name, credentialValue(integration, field.name)]; })
        .filter(function (_a) {
        var value = _a[1];
        return hasValue(value);
    }));
};
/**
 * Per-field credential problems, keyed by field name.
 *
 * Only format errors are reported: a missing required field already disables Connect, so flagging
 * untouched inputs would be noise. URL format is checked because the API rejects anything that isn't an
 * absolute http(s) URL, and without this the user would only see an opaque failure after submitting.
 */
var credentialFieldErrors = function (type, form) {
    var _a;
    var shape = Object.fromEntries(credentialFieldsFor(type)
        .filter(function (field) { return field.kind === 'url'; })
        // https only, matching the API: it rejects http and non-public hosts, so accepting them here
        // would just turn a typo into an opaque request failure.
        .map(function (field) {
        return [
            field.name,
            yup
                .string()
                .url()
                .matches(/^https:\/\//i),
        ];
    }));
    if (!Object.keys(shape).length)
        return {};
    var supplied = Object.fromEntries(Object.keys(shape)
        .map(function (name) { return [name, credentialValue(form, name)]; })
        .filter(function (_a) {
        var value = _a[1];
        return hasValue(value);
    }));
    try {
        yup.object(shape).validateSync(supplied, { abortEarly: false });
        return {};
    }
    catch (error) {
        var validationError = error;
        return Object.fromEntries(((_a = validationError.inner) !== null && _a !== void 0 ? _a : []).filter(function (e) { return e.path; }).map(function (e) { return [e.path, 'invalidUrl']; }));
    }
};
var resolveAlertMessage = function (testResult, t) {
    return (testResult === null || testResult === void 0 ? void 0 : testResult.status) === 'success'
        ? t('modal.connectionSuccess')
        : t('modal.connectionFailed', { context: testResult === null || testResult === void 0 ? void 0 : testResult.reason });
};
var mergeCredentialsIntoIntegration = function (prev, maskedCredentials) {
    var _a, _b, _c;
    if (prev.type === 'genesys' && !prev.clientId && maskedCredentials.clientId) {
        return __assign(__assign({}, prev), { clientId: maskedCredentials.clientId, region: (_a = maskedCredentials.region) !== null && _a !== void 0 ? _a : prev.region });
    }
    if (prev.type === 'aspect' && !prev.tokenUrl && maskedCredentials.tokenUrl) {
        return __assign(__assign({}, prev), { apiBaseUrl: (_b = maskedCredentials.apiBaseUrl) !== null && _b !== void 0 ? _b : prev.apiBaseUrl, tokenUrl: maskedCredentials.tokenUrl, clientId: (_c = maskedCredentials.clientId) !== null && _c !== void 0 ? _c : prev.clientId });
    }
    return prev;
};
/**
 * Runs `onResolved` once a request has produced data, then clears the request state on the
 * next commit (unmount / re-run) — the pattern every API call in this form follows.
 *
 * The handler is held in a ref so it always reads the latest closure (e.g. current `onSaved`,
 * `integration.type`) without being a dependency; the effect re-runs only when `data` or one
 * of the `clears` changes. The clears are keyed by step2ClientId, which is what gates the
 * cleanup correctly (see the onOpenRef note above).
 */
function useOnResolved(data, onResolved, clears) {
    var onResolvedRef = useRef(onResolved);
    onResolvedRef.current = onResolved;
    useEffect(function () {
        if (data)
            onResolvedRef.current(data);
        return function () { return clears.forEach(function (clear) { return clear(); }); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, __spreadArray([data], clears, true));
}
export var useAddClientForm = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    var open = _a.open, onClose = _a.onClose, onSaved = _a.onSaved, configVersionId = _a.configVersionId, editClient = _a.editClient, _o = _a.mode, mode = _o === void 0 ? editClient ? 'edit-details' : 'add-client' : _o, targetIntegrationId = _a.targetIntegrationId, t = _a.t;
    var schema = yup.object({
        clientName: yup.string().required().trim().min(1).label(t('modal.clientName')),
        industryId: yup.number().required().min(1).label(t('modal.industry')),
    });
    var _p = useForm({
        defaultValues: { clientName: '', industryId: '' },
        resolver: yupResolver(schema),
    }), control = _p.control, handleSubmit = _p.handleSubmit, reset = _p.reset, watch = _p.watch;
    var clientNameValue = watch('clientName');
    var industryIdValue = watch('industryId');
    var _q = useState(1), step = _q[0], setStep = _q[1];
    var _r = useState(null), integrationTypeError = _r[0], setIntegrationTypeError = _r[1];
    var _s = useState(false), connectionRequired = _s[0], setConnectionRequired = _s[1];
    var _t = useState(defaultIntegration), integration = _t[0], setIntegration = _t[1];
    var _u = useIndustryAPI(), industriesData = _u[0], fetchIndustries = _u[1];
    var _v = useState(null), testResult = _v[0], setTestResult = _v[1];
    var _w = useState(null), savedClientId = _w[0], setSavedClientId = _w[1];
    var _x = useState(false), showClientId = _x[0], setShowClientId = _x[1];
    var _y = useState(0), step2ClientId = _y[0], setStep2ClientId = _y[1];
    var _z = useState(0), activeIntegrationId = _z[0], setActiveIntegrationId = _z[1];
    var _0 = useCreateClientAPI(), createData = _0[0], createClient = _0[1], clearCreateData = _0[2];
    var _1 = useClientDetailAPI((_b = editClient === null || editClient === void 0 ? void 0 : editClient.id) !== null && _b !== void 0 ? _b : null), clientDetailData = _1[0], fetchClientDetail = _1[1], clearClientDetail = _1[2];
    var _2 = useUpdateClientAPI((_c = editClient === null || editClient === void 0 ? void 0 : editClient.id) !== null && _c !== void 0 ? _c : 0), updateData = _2[0], updateClient = _2[1], clearUpdateData = _2[2];
    var _3 = useTestConnectionAPI(), testData = _3[0], testConnection = _3[1], clearTestData = _3[2];
    var _4 = useTestAndSaveConnectionAPI((_d = editClient === null || editClient === void 0 ? void 0 : editClient.id) !== null && _d !== void 0 ? _d : 0), testSaveData = _4[0], testAndSave = _4[1], clearTestSaveData = _4[2];
    var _5 = useDisconnectIntegrationAPI((_e = editClient === null || editClient === void 0 ? void 0 : editClient.id) !== null && _e !== void 0 ? _e : 0, activeIntegrationId), disconnectData = _5[0], disconnectIntegration = _5[1], clearDisconnectData = _5[2];
    var _6 = useRetestConnectionAPI((_f = editClient === null || editClient === void 0 ? void 0 : editClient.id) !== null && _f !== void 0 ? _f : 0, activeIntegrationId), retestData = _6[0], retestConnection = _6[1], clearRetestData = _6[2];
    var _7 = useAvailableBusinessUnitsAPI(step2ClientId, activeIntegrationId), availableBusinessUnitsData = _7[0], fetchAvailableBusinessUnits = _7[1], clearAvailableBusinessUnits = _7[2];
    var _8 = useClientBusinessUnitsAPI(step2ClientId, 'orgConfigSavedBus'), savedBusData = _8[0], fetchSavedBus = _8[1], clearSavedBus = _8[2];
    var _9 = useImportBusinessUnitsAPI(step2ClientId), importData = _9[0], importBusinessUnits = _9[1], clearImportData = _9[2];
    var businessUnits = useMemo(function () { var _a, _b; return (_b = (_a = availableBusinessUnitsData.data) === null || _a === void 0 ? void 0 : _a.businessUnits) !== null && _b !== void 0 ? _b : []; }, [(_g = availableBusinessUnitsData.data) === null || _g === void 0 ? void 0 : _g.businessUnits]);
    var _10 = useBusinessUnitSelection({
        businessUnits: businessUnits,
    }), selected = _10.selected, setSelected = _10.setSelected, allSelected = _10.allSelected, someSelected = _10.someSelected, toggleSelectAll = _10.toggleSelectAll, toggleRow = _10.toggleRow;
    var isEdit = !!editClient;
    var initializeFormState = useCallback(function () {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (editClient) {
            reset({ clientName: editClient.clientName, industryId: (_a = editClient.industryId) !== null && _a !== void 0 ? _a : '' });
            setSavedClientId(editClient.id);
            // edit-integration targets one specific connection (prefill its name + masked creds). Every
            // other mode on an existing client (edit-details / add-integration) starts from a blank one.
            if (mode === 'edit-integration' && targetIntegrationId) {
                var target = findClientIntegrationById(editClient, targetIntegrationId);
                var masked = (_b = target === null || target === void 0 ? void 0 : target.maskedCredentials) !== null && _b !== void 0 ? _b : {};
                setIntegration(__assign(__assign({}, defaultIntegration), { type: (_c = target === null || target === void 0 ? void 0 : target.integrationType) !== null && _c !== void 0 ? _c : '', name: (_d = target === null || target === void 0 ? void 0 : target.name) !== null && _d !== void 0 ? _d : '', clientId: (_e = masked.clientId) !== null && _e !== void 0 ? _e : '', region: (_f = masked.region) !== null && _f !== void 0 ? _f : defaultIntegration.region, apiBaseUrl: (_g = masked.apiBaseUrl) !== null && _g !== void 0 ? _g : '', tokenUrl: (_h = masked.tokenUrl) !== null && _h !== void 0 ? _h : '' }));
                setActiveIntegrationId(targetIntegrationId);
                setTestResult(getInitialTestResult(target === null || target === void 0 ? void 0 : target.integrationStatus));
            }
            else {
                setIntegration(defaultIntegration);
                setActiveIntegrationId(0);
                setTestResult(null);
            }
        }
        else {
            reset({ clientName: '', industryId: '' });
            setIntegration(defaultIntegration);
            setSavedClientId(null);
            setActiveIntegrationId(0);
            setTestResult(null);
        }
    }, [editClient, mode, targetIntegrationId, reset]);
    var isSaving = !!(createData.requesting || updateData.requesting);
    var isTesting = !!(testData.requesting || testSaveData.requesting);
    var businessUnitsFetchExpected = step2ClientId > 0 && activeIntegrationId > 0;
    var businessUnitsSettled = availableBusinessUnitsData.data !== undefined || !!availableBusinessUnitsData.error;
    var isLoading = !!availableBusinessUnitsData.requesting || (businessUnitsFetchExpected && !businessUnitsSettled);
    var isImporting = !!importData.requesting;
    var isDisconnecting = !!disconnectData.requesting;
    var isRetesting = !!retestData.requesting;
    // onOpenRef holds the latest reset logic so the effect only depends on `open`.
    // clearImportData/clearAvailableBusinessUnits/clearSavedBus are keyed by step2ClientId and get new
    // references every time step2ClientId changes — including right after clicking "Next" —
    // which would cause the effect to re-run and reset the modal back to step 1.
    var onOpenRef = useRef(function () { });
    onOpenRef.current = function () {
        fetchIndustries();
        setStep(1);
        setStep2ClientId(0);
        setShowClientId(false);
        setSelected(new Set());
        setIntegrationTypeError(null);
        setConnectionRequired(false);
        initializeFormState();
        clearCreateData();
        clearClientDetail();
        clearUpdateData();
        clearTestData();
        clearTestSaveData();
        clearDisconnectData();
        clearRetestData();
        clearImportData();
        clearAvailableBusinessUnits();
        clearSavedBus();
    };
    useEffect(function () {
        if (open)
            onOpenRef.current();
    }, [open]);
    // Selecting a different integration type clears any prior "connection required" block.
    useEffect(function () {
        setConnectionRequired(false);
    }, [integration.type]);
    useEffect(function () {
        var target = findClientIntegrationById(editClient, targetIntegrationId);
        if (needsFetchClientDetail(open, editClient, target, integration.type, integration.clientId, integration.tokenUrl)) {
            fetchClientDetail();
        }
    }, [
        open,
        editClient,
        targetIntegrationId,
        integration.type,
        integration.clientId,
        integration.tokenUrl,
        fetchClientDetail,
    ]);
    useEffect(function () {
        var _a;
        if (!open || !editClient || !clientDetailData.data)
            return;
        var masked = (_a = findClientIntegrationById(clientDetailData.data, targetIntegrationId)) === null || _a === void 0 ? void 0 : _a.maskedCredentials;
        if (!masked)
            return;
        setIntegration(function (prev) { return mergeCredentialsIntoIntegration(prev, masked); });
    }, [open, editClient, clientDetailData.data, targetIntegrationId]);
    useEffect(function () {
        if (step2ClientId > 0 && activeIntegrationId > 0) {
            fetchAvailableBusinessUnits();
            if (isEdit) {
                fetchSavedBus();
            }
        }
    }, [step2ClientId, activeIntegrationId, isEdit, fetchAvailableBusinessUnits, fetchSavedBus]);
    // Pre-select the Business Units already imported from this connection (edit mode), keyed by
    // externalId. Other connections' BUs (even of the same type) must not pre-select anything.
    useEffect(function () {
        if (!isEdit || step2ClientId === 0 || !savedBusData.data || !availableBusinessUnitsData.data) {
            return;
        }
        var importedExternalIds = new Set(savedBusData.data
            .filter(function (bu) { return bu.integrationId === activeIntegrationId; })
            .map(function (bu) { return bu.externalBuId; })
            .filter(Boolean));
        var preSelected = new Set(businessUnits.filter(function (bu) { return importedExternalIds.has(bu.externalId); }).map(function (bu) { return bu.externalId; }));
        setSelected(preSelected);
    }, [
        isEdit,
        step2ClientId,
        activeIntegrationId,
        savedBusData.data,
        availableBusinessUnitsData.data,
        businessUnits,
        setSelected,
    ]);
    var hasCredentialFields = integration.type === 'genesys' || integration.type === 'aspect';
    // Only meaningful in edit-integration mode: the targeted row is persisted server-side. In
    // add-integration mode a second connection of an already-connected type is still NEW.
    var targetStatus = mode === 'edit-integration'
        ? (_h = findClientIntegrationById(editClient, targetIntegrationId)) === null || _h === void 0 ? void 0 : _h.integrationStatus
        : undefined;
    var isAlreadyConnected = targetStatus === 'connected' || targetStatus === 'failed';
    var isActiveConnection = targetStatus === 'connected';
    var connectionSucceeded = (testResult === null || testResult === void 0 ? void 0 : testResult.status) === 'success';
    var isConnected = connectionSucceeded || isActiveConnection;
    var showNextButton = hasCredentialFields && isConnected;
    var requiredClientFieldsComplete = !!(clientNameValue === null || clientNameValue === void 0 ? void 0 : clientNameValue.trim()) && !!industryIdValue;
    var canSubmitStep1 = requiredClientFieldsComplete && (!hasCredentialFields || isConnected);
    // Resolve-on-data effects: each clears its own request state on unmount/re-run.
    useOnResolved(createData.data, function (client) {
        var _a, _b, _c;
        setSavedClientId(client.id);
        if (isActualIntegration(integration.type)) {
            setActiveIntegrationId((_c = (_b = (_a = client.integrations) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : 0);
            setStep2ClientId(client.id);
            setStep(2);
        }
        else {
            onSaved();
            onClose();
        }
    }, [clearCreateData]);
    useOnResolved(updateData.data, function () {
        onSaved();
        onClose();
    }, [clearUpdateData]);
    useOnResolved(testData.data, function (result) { return setTestResult(result); }, [clearTestData]);
    // Disconnect returns 204 (no body), so success is detected via status code rather than data.
    useEffect(function () {
        var ok = disconnectData.statusCode != null && disconnectData.statusCode >= 200 && disconnectData.statusCode < 300;
        if (ok) {
            onSaved();
            onClose();
        }
        return clearDisconnectData;
    }, [disconnectData.statusCode, onSaved, onClose, clearDisconnectData]);
    useOnResolved(testSaveData.data, function (result) {
        setTestResult(result);
        if (result.integrationId)
            setActiveIntegrationId(result.integrationId);
        onSaved();
    }, [clearTestSaveData]);
    useOnResolved(retestData.data, function (result) {
        setTestResult(result);
        onSaved();
    }, [clearRetestData]);
    useOnResolved(importData.data, function () {
        onSaved();
        onClose();
    }, [clearImportData]);
    var resolvedConnectedClientId = integration.clientId ||
        ((_k = (_j = findClientIntegrationById(clientDetailData.data, targetIntegrationId)) === null || _j === void 0 ? void 0 : _j.maskedCredentials) === null || _k === void 0 ? void 0 : _k.clientId) ||
        ((_m = (_l = findClientIntegrationById(editClient, targetIntegrationId)) === null || _l === void 0 ? void 0 : _l.maskedCredentials) === null || _m === void 0 ? void 0 : _m.clientId) ||
        '';
    var credentialsComplete = areCredentialsComplete(integration.type, integration, isAlreadyConnected);
    var credentialErrors = credentialFieldErrors(integration.type, integration);
    // A malformed URL must block Connect too — the API rejects it, so sending it would only surface a 400.
    var credentialsValid = credentialsComplete && !Object.keys(credentialErrors).length;
    var handleConnect = function () {
        var credentials = buildCredentials(integration, isAlreadyConnected);
        if (!credentials)
            return;
        setTestResult(null);
        if (isEdit) {
            testAndSave(__assign(__assign({ integrationType: integration.type }, (activeIntegrationId ? { integrationId: activeIntegrationId } : {})), { name: integration.name.trim(), credentials: credentials }));
            return;
        }
        testConnection({ integrationType: integration.type, credentials: credentials });
    };
    var handleRetest = function () {
        if (!(isEdit && activeIntegrationId > 0))
            return;
        setTestResult(null);
        retestConnection();
    };
    var handleDisconnect = function () {
        // A persisted connection (already on the client) is removed server-side, and the
        // disconnect-success effect closes the modal — so we must NOT locally reset the form. Resetting
        // would briefly leave a disabled, empty "Please select" dropdown (it's locked in edit-integration)
        // and a Save that then errors with "Integration is required". Only clear locally for an unsaved
        // selection (add-client, or a freshly-picked type that was never connected). A connection created
        // mid-modal by testAndSave (add-integration) is persisted too — activeIntegrationId covers both.
        var persisted = isEdit && activeIntegrationId > 0;
        if (persisted) {
            disconnectIntegration();
            return;
        }
        setIntegration(defaultIntegration);
        setTestResult(null);
    };
    var showConnectionRequired = connectionRequired && hasCredentialFields && !isConnected;
    var handleFormSubmit = function (_a) {
        var _b;
        var clientName = _a.clientName, industryId = _a.industryId;
        // edit-details only changes the client's name/industry; integrations are managed in their own modes.
        if (mode === 'edit-details') {
            updateClient({ clientName: clientName, industryId: industryId === '' ? null : industryId });
            return;
        }
        if (!integration.type) {
            setIntegrationTypeError(t('modal.integrationRequired'));
            return;
        }
        setIntegrationTypeError(null);
        // An integration that requires credentials must be connected (freshly tested, or already
        // connected from a prior session) before saving — prevents persisting an unverified integration
        // with a false "connected" status. Already-connected integrations don't need a re-test.
        if (hasCredentialFields && !isConnected) {
            setConnectionRequired(true);
            return;
        }
        setConnectionRequired(false);
        var resolvedIndustryId = industryId === '' ? null : industryId;
        if (showNextButton) {
            if (!isEdit && !savedClientId) {
                createClient({
                    configVersionId: configVersionId,
                    clientName: clientName,
                    industryId: resolvedIndustryId,
                    integrationType: integration.type,
                    integrationName: integration.name.trim(),
                    integrationCredentials: buildCredentials(integration, isAlreadyConnected),
                });
                return;
            }
            setStep2ClientId((_b = savedClientId !== null && savedClientId !== void 0 ? savedClientId : editClient === null || editClient === void 0 ? void 0 : editClient.id) !== null && _b !== void 0 ? _b : 0);
            setStep(2);
            return;
        }
        // Edit only saves client fields; integrations are managed via Connect (testAndSave) and the
        // Disconnect button, not through updateClient (whose DTO no longer accepts integration fields).
        if (isEdit) {
            updateClient({ clientName: clientName, industryId: resolvedIndustryId });
            return;
        }
        var integrationPayload = isActualIntegration(integration.type)
            ? {
                integrationType: integration.type,
                integrationName: integration.name.trim(),
                integrationCredentials: buildCredentials(integration, isAlreadyConnected),
            }
            : { integrationType: null, integrationName: null, integrationCredentials: null };
        createClient(__assign({ configVersionId: configVersionId, clientName: clientName, industryId: resolvedIndustryId }, integrationPayload));
    };
    var handleImport = function () {
        var businessUnitsToImport = businessUnits
            .filter(function (bu) { return selected.has(bu.externalId); })
            .map(function (bu) { return ({ externalId: bu.externalId, name: bu.name }); });
        importBusinessUnits({ integrationId: activeIntegrationId, businessUnits: businessUnitsToImport });
    };
    var maskedOrVisible = showClientId ? resolvedConnectedClientId : MASKED_SECRET;
    var genesysClientIdValue = isAlreadyConnected ? maskedOrVisible : integration.clientId;
    var alertMessage = resolveAlertMessage(testResult, t);
    var showNoBusinessUnitsAlert = businessUnitsSettled && businessUnits.length === 0 && !availableBusinessUnitsData.error;
    var terminologyContext = isActualIntegration(integration.type) ? camelCase(integration.type) : undefined;
    return {
        // step 1 form
        control: control,
        clientNameValue: clientNameValue,
        submitStep1: handleSubmit(handleFormSubmit),
        // step navigation
        step: step,
        setStep: setStep,
        // integration form state
        integration: integration,
        setIntegration: setIntegration,
        testResult: testResult,
        setTestResult: setTestResult,
        showClientId: showClientId,
        setShowClientId: setShowClientId,
        integrationTypeError: integrationTypeError,
        setIntegrationTypeError: setIntegrationTypeError,
        // derived integration flags
        hasCredentialFields: hasCredentialFields,
        isAlreadyConnected: isAlreadyConnected,
        credentialsComplete: credentialsValid,
        credentialErrors: credentialErrors,
        showNextButton: showNextButton,
        canSubmitStep1: canSubmitStep1,
        showConnectionRequired: showConnectionRequired,
        genesysClientIdValue: genesysClientIdValue,
        alertMessage: alertMessage,
        setConnectionRequired: setConnectionRequired,
        // integration actions
        handleConnect: handleConnect,
        handleDisconnect: handleDisconnect,
        handleRetest: handleRetest,
        handleImport: handleImport,
        // request status flags
        isSaving: isSaving,
        isTesting: isTesting,
        isLoading: isLoading,
        isImporting: isImporting,
        isDisconnecting: isDisconnecting,
        isRetesting: isRetesting,
        // request states (errors are surfaced in the UI)
        createData: createData,
        updateData: updateData,
        testData: testData,
        testSaveData: testSaveData,
        disconnectData: disconnectData,
        retestData: retestData,
        availableBusinessUnitsData: availableBusinessUnitsData,
        importData: importData,
        industriesData: industriesData,
        // step 2 business unit selection
        businessUnits: businessUnits,
        selected: selected,
        allSelected: allSelected,
        someSelected: someSelected,
        toggleSelectAll: toggleSelectAll,
        toggleRow: toggleRow,
        showNoBusinessUnitsAlert: showNoBusinessUnitsAlert,
        terminologyContext: terminologyContext,
    };
};
